import { __extends } from "tslib";
import { MDCNotchedOutline } from '@material/notched-outline';
import { calculateFontWidth, getShadowElement, Platform } from '@tyler-components-web/core';
import { toggleClass, toggleAttribute } from '../../core/utils';
import { FloatingLabel } from '../../floating-label/floating-label';
import { SELECT_CONSTANTS } from './select-constants';
import { BaseSelectAdapter } from '../core';
/**
 * The DOM adapter behind the `<tcw-select>` component.
 */
var SelectAdapter = /** @class */ (function (_super) {
    __extends(SelectAdapter, _super);
    function SelectAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._selectElement = getShadowElement(component, SELECT_CONSTANTS.selectors.ROOT);
        _this._notchedOutlineElement = getShadowElement(component, SELECT_CONSTANTS.selectors.NOTCHED_OUTLINE);
        _this._leadingSlot = getShadowElement(component, SELECT_CONSTANTS.selectors.LEADING_SLOT);
        _this._addonEndSlot = getShadowElement(_this._component, SELECT_CONSTANTS.selectors.ADDON_END_SLOT);
        _this._selectedTextElement = getShadowElement(component, SELECT_CONSTANTS.selectors.SELECTED_TEXT);
        _this._targetElement = _this._selectElement;
        return _this;
    }
    SelectAdapter.prototype.initializeLabel = function () {
        this._labelElement = getShadowElement(this._component, SELECT_CONSTANTS.selectors.LABEL);
    };
    SelectAdapter.prototype.hasLabel = function () {
        return !!this._labelElement;
    };
    SelectAdapter.prototype.initializeAccessibility = function () {
        this._component.setAttribute('role', 'combobox');
        this._component.setAttribute('aria-live', 'assertive');
        this._component.setAttribute('aria-atomic', 'true');
        this._component.setAttribute('aria-haspopup', 'true');
        this._component.setAttribute('aria-expanded', 'false');
    };
    SelectAdapter.prototype.initializeNotchedOutline = function () {
        if (!this._notchedOutlineElement) {
            return;
        }
        return new MDCNotchedOutline(this._notchedOutlineElement);
    };
    SelectAdapter.prototype.initializeFloatingLabel = function () {
        return new FloatingLabel(this._labelElement);
    };
    SelectAdapter.prototype.setLabel = function (value) {
        if (!this._component.hasAttribute('aria-label') || this._component.getAttribute('aria-label') === this._labelElement.textContent) {
            this._component.setAttribute('aria-label', value);
        }
        this._labelElement.textContent = value;
    };
    SelectAdapter.prototype.addRootClass = function (name) {
        this._selectElement.classList.add(name);
    };
    SelectAdapter.prototype.removeRootClass = function (name) {
        this._selectElement.classList.remove(name);
    };
    SelectAdapter.prototype.setPlaceholderText = function (value) {
        toggleAttribute(this._selectedTextElement, !!value, 'placeholder', value);
    };
    SelectAdapter.prototype.addClickListener = function (listener) {
        this._selectElement.addEventListener('click', listener);
    };
    SelectAdapter.prototype.removeClickListener = function (listener) {
        this._selectElement.removeEventListener('click', listener);
    };
    SelectAdapter.prototype.addMouseDownListener = function (listener) {
        this._selectElement.addEventListener('mousedown', listener);
    };
    SelectAdapter.prototype.removeMouseDownListener = function (listener) {
        this._selectElement.removeEventListener('mousedown', listener);
    };
    SelectAdapter.prototype.addTargetListener = function (type, listener) {
        this._selectedTextElement.addEventListener(type, listener);
    };
    SelectAdapter.prototype.removeTargetListener = function (type, listener) {
        this._selectedTextElement.removeEventListener(type, listener);
    };
    SelectAdapter.prototype.open = function (config) {
        _super.prototype.open.call(this, config);
        this._component.setAttribute('aria-controls', "list-dropdown-popup-" + config.id);
        this._component.setAttribute('aria-expanded', 'true');
        toggleClass(this._selectElement, true, SELECT_CONSTANTS.classes.OPENED);
    };
    SelectAdapter.prototype.close = function () {
        this._component.setAttribute('aria-expanded', 'false');
        this._component.removeAttribute('aria-activedescendant');
        this._component.removeAttribute('aria-controls');
        toggleClass(this._selectElement, false, SELECT_CONSTANTS.classes.OPENED);
        _super.prototype.close.call(this);
    };
    SelectAdapter.prototype.updateActiveDescendant = function (id) {
        toggleAttribute(this._component, !!id, 'aria-activedescendant', id);
    };
    SelectAdapter.prototype.setSelectedText = function (value) {
        var _this = this;
        // We wait until the next frame to update the selected text in IE/Edge to avoid a conflict with
        // the web component polyfills hanging when updating the text in the same frame
        if (Platform.TRIDENT || Platform.EDGE) {
            setTimeout(function () { return (_this._selectedTextElement.textContent = value); });
        }
        else {
            this._selectedTextElement.textContent = value;
        }
    };
    SelectAdapter.prototype.setLeadingListener = function (listener) {
        this._leadingSlot.addEventListener('slotchange', listener);
    };
    SelectAdapter.prototype.removeLeadingListener = function (listener) {
        this._leadingSlot.removeEventListener('slotchange', listener);
    };
    SelectAdapter.prototype.setAddonEndListener = function (listener) {
        this._addonEndSlot.addEventListener('slotchange', listener);
    };
    SelectAdapter.prototype.removeAddonEndListener = function (listener) {
        this._addonEndSlot.removeEventListener('slotchange', listener);
    };
    SelectAdapter.prototype.hasLeadingElement = function () {
        return this._leadingSlot.assignedNodes().length > 0;
    };
    SelectAdapter.prototype.setDisabled = function (isDisabled) {
        toggleClass(this._selectElement, isDisabled, SELECT_CONSTANTS.classes.SELECT_DISABLED);
        toggleAttribute(this._component, isDisabled, 'aria-disabled', 'true');
        this._selectedTextElement.tabIndex = isDisabled ? -1 : 0;
    };
    SelectAdapter.prototype.setInvalid = function (isInvalid) {
        toggleClass(this._selectElement, isInvalid, SELECT_CONSTANTS.classes.INVALID);
        toggleAttribute(this._component, isInvalid, 'aria-invalid', 'true');
    };
    SelectAdapter.prototype.setRequired = function (isRequired) {
        toggleClass(this._selectElement, isRequired, SELECT_CONSTANTS.classes.REQUIRED);
        toggleAttribute(this._component, isRequired, 'aria-required', 'true');
    };
    SelectAdapter.prototype.setDense = function (isDense) {
        toggleClass(this._selectElement, isDense, SELECT_CONSTANTS.classes.DENSE);
    };
    SelectAdapter.prototype.setRoomy = function (isRoomy) {
        toggleClass(this._selectElement, isRoomy, SELECT_CONSTANTS.classes.ROOMY);
    };
    SelectAdapter.prototype.setInset = function (isInset) {
        toggleClass(this._selectElement, isInset, SELECT_CONSTANTS.classes.INSET);
    };
    SelectAdapter.prototype.setMultiple = function (multiple) {
        if (multiple) {
            this.setHostAttribute('aria-multiselectable', 'true');
        }
        else {
            this.removeHostAttribute('aria-multiselectable');
        }
    };
    SelectAdapter.prototype.getLabelWidth = function (fontSize, fontFamily) {
        return calculateFontWidth(this._labelElement.innerText, { fontSize: fontSize, fontFamily: fontFamily });
    };
    SelectAdapter.prototype.getLabelFontMetrics = function () {
        var style = getComputedStyle(this._labelElement);
        return {
            fontSize: parseInt(style.fontSize || '16', 10),
            fontFamily: style.fontFamily || 'Roboto'
        };
    };
    SelectAdapter.prototype.getTargetWidth = function () {
        return this._selectElement.getBoundingClientRect().width;
    };
    SelectAdapter.prototype.hasAddonEndNodes = function () {
        if (!this._addonEndSlot) {
            return false;
        }
        return this._addonEndSlot.assignedNodes().length > 0;
    };
    SelectAdapter.prototype.setFocus = function () {
        this._selectedTextElement.focus();
    };
    SelectAdapter.prototype.isWithinSelf = function (element) {
        var isShadowChild = !!this._component.shadowRoot && this._component.shadowRoot.contains(element);
        return isShadowChild || this._component.contains(element);
    };
    return SelectAdapter;
}(BaseSelectAdapter));
export { SelectAdapter };
